import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowDownCircle, ArrowUpCircle, Settings2, Package, Utensils, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ExpenseStatusBadge } from "@/components/expenses/ExpenseStatusBadge";

interface Movement {
  id: string;
  type: string;
  quantity: number;
  previous_stock: number;
  new_stock: number;
  reason: string | null;
  cost: number | null;
  unit_cost?: number;
  total_cost?: number;
  created_at: string;
  ingredients: {
    name: string;
    unit: string;
  } | null;
  products: {
    id: string;
    name: string;
  } | null;
  expenses?: {
    id: string;
    title: string;
    status: "open" | "paid" | "cancelled";
    due_date?: string | null;
  } | null;
}

interface StockMovementsListProps {
  movements: Movement[];
  getMovementTypeLabel: (type: string) => string;
  isLoading?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalMovements?: number;
  onPageChange?: (page: number) => void;
}

const typeConfig: Record<string, { icon: any; color: string; bgColor: string }> = {
  entry: {
    icon: ArrowDownCircle,
    color: "text-green-500",
    bgColor: "bg-green-500/10",
  },
  exit: {
    icon: ArrowUpCircle,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  adjustment: {
    icon: Settings2,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  sale: {
    icon: ArrowUpCircle,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
};

export function StockMovementsList({
  movements,
  getMovementTypeLabel,
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  totalMovements = 0,
  onPageChange,
}: StockMovementsListProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getItemInfo = (movement: Movement) => {
    if (movement.ingredients) {
      return {
        name: movement.ingredients.name,
        unit: movement.ingredients.unit,
        type: "ingredient" as const,
        label: "Ingrediente",
      };
    }
    if (movement.products) {
      return {
        name: movement.products.name,
        unit: "un",
        type: "product" as const,
        label: "Produto",
      };
    }
    return {
      name: "—",
      unit: "un",
      type: "unknown" as const,
      label: "—",
    };
  };

  // Calculate totals for visible movements
  const totals = movements.reduce(
    (acc, movement) => {
      const qty = Math.abs(movement.quantity);
      const cost = movement.total_cost || 0;
      
      if (movement.type === "entry") {
        acc.entries += qty;
        acc.entriesCost += cost;
      } else if (movement.type === "exit" || movement.type === "sale") {
        acc.exits += qty;
        acc.exitsCost += cost;
      }
      return acc;
    },
    { entries: 0, exits: 0, entriesCost: 0, exitsCost: 0 }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 border rounded-lg">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Carregando movimentações...</span>
      </div>
    );
  }

  if (movements.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">Nenhuma movimentação encontrada</p>
        <p className="text-sm text-muted-foreground mt-1">
          Ajuste os filtros ou registre novas movimentações
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Total Registros</p>
            <p className="text-lg font-bold">{totalMovements}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-green-600">Entradas (página)</p>
            <p className="text-lg font-bold text-green-600">+{totals.entries.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(totals.entriesCost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-red-600">Saídas/Vendas (página)</p>
            <p className="text-lg font-bold text-red-600">-{totals.exits.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(totals.exitsCost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Página</p>
            <p className="text-lg font-bold">{currentPage} / {totalPages}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="text-center">Tipo</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              <TableHead className="text-right">Antes</TableHead>
              <TableHead className="text-right">Depois</TableHead>
              <TableHead className="text-right">Custo</TableHead>
              <TableHead>Conta a pagar</TableHead>
              <TableHead>Motivo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.map((movement) => {
              const config = typeConfig[movement.type] || typeConfig.adjustment;
              const Icon = config.icon;
              const itemInfo = getItemInfo(movement);

              return (
                <TableRow key={movement.id}>
                  <TableCell className="text-sm">
                    {format(new Date(movement.created_at), "dd/MM/yyyy HH:mm", {
                      locale: ptBR,
                    })}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "gap-1",
                        itemInfo.type === "ingredient"
                          ? "border-amber-500/50 text-amber-600 bg-amber-500/10"
                          : itemInfo.type === "product"
                          ? "border-blue-500/50 text-blue-600 bg-blue-500/10"
                          : ""
                      )}
                    >
                      {itemInfo.type === "ingredient" ? (
                        <Utensils className="h-3 w-3" />
                      ) : (
                        <Package className="h-3 w-3" />
                      )}
                      {itemInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{itemInfo.name}</p>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className={cn("p-1 rounded", config.bgColor)}>
                        <Icon className={cn("h-4 w-4", config.color)} />
                      </div>
                      <span className="text-sm">{getMovementTypeLabel(movement.type)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <span className={cn(
                      movement.quantity > 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {movement.quantity > 0 ? "+" : ""}
                      {itemInfo.type === "ingredient" 
                        ? movement.quantity.toFixed(2) 
                        : movement.quantity
                      } {itemInfo.unit}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {itemInfo.type === "ingredient"
                      ? movement.previous_stock.toFixed(2)
                      : movement.previous_stock
                    }
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {itemInfo.type === "ingredient"
                      ? movement.new_stock.toFixed(2)
                      : movement.new_stock
                    }
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {movement.total_cost ? formatCurrency(movement.total_cost) : "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {movement.expenses ? (
                      <div className="flex flex-col gap-1 max-w-[220px]">
                        <p className="font-medium truncate" title={movement.expenses.title}>
                          {movement.expenses.title}
                        </p>
                        <div className="w-fit">
                          <ExpenseStatusBadge status={movement.expenses.status} />
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {movement.reason || "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {movements.length} de {totalMovements} registros
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm px-3">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Próxima
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
